import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData, API_BASE_URL, API_TOKEN } from '../helpers/ApiSettings';
import LoisirProductTable from '../components/LoisirProducts/LoisirProductTable';
import AddLoisirProductModal from '../components/LoisirProducts/AddLoisirProductModal';
import EditLoisirProductModal from '../components/LoisirProducts/EditLoisirProductModal';

const LoisirProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  const [newProduct, setNewProduct] = useState({
    title: '',
    description: '',
    reference: '',
    minimumAge: '',
    quantity: 1, // New field for product quantity
    available: true
  });

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await getData('/loisir-products?populate=*');
      console.log(response.data)
      setProducts(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des produits loisirs');
      console.error('Error fetching loisir products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleAddProduct = async (e, productData) => {
    e.preventDefault();
    try {
      const { files, ...productDetails } = productData;
      const response = await postData('/loisir-products', {
        data: productDetails
      });

      const newProductId = response.data.data.id;

      if (files && files.length > 0) {
        await handleImageUpload(newProductId, files);
      }

      await fetchProducts();
      setNewProduct({
        title: '',
        description: '',
        reference: '',
        minimumAge: '',
        quantity: 1,
        available: true
      });

      window.$('#addLoisirProductModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Produit loisir ajouté avec succès');
    } catch (err) {
      alert("Erreur lors de l'ajout du produit loisir");
      console.error('Error adding loisir product:', err);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        title: editingProduct.title,
        description: editingProduct.description,
        reference: editingProduct.reference,
        minimumAge: editingProduct.minimumAge,
        quantity: editingProduct.quantity, // Include quantity field
        available: editingProduct.available
      };

      await putData(`/loisir-products/${editingProduct.documentId}`, {
        data: updateData
      });

      if (editingProduct.newImages && editingProduct.newImages.length > 0) {
        await handleImageUpload(editingProduct.id, editingProduct.newImages);
      }

      await fetchProducts();
      window.$('#editLoisirProductModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      setEditingProduct(null);
      alert('Produit loisir mis à jour avec succès');
    } catch (err) {
      console.error('Error updating loisir product:', err.response?.data || err);
      alert("Erreur lors de la mise à jour du produit loisir: " + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit loisir ?')) {
      try {
        await deleteData(`/loisir-products/${id}`);
        await fetchProducts();
        alert('Produit loisir supprimé avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression du produit loisir');
        console.error('Error deleting loisir product:', err);
      }
    }
  };

  const handleEditClick = (product) => {
    const productCopy = JSON.parse(JSON.stringify(product));
    setEditingProduct(productCopy);
    window.$('#editLoisirProductModal').modal('show');
  };

  const handleImageUpload = async (productId, files) => {
    try {
      const formData = new FormData();
      formData.append('ref', 'api::loisir-product.loisir-product');
      formData.append('refId', productId);
      formData.append('field', 'images');

      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      await fetchProducts();
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Erreur lors du téléchargement des images');
      throw error;
    }
  };

  const handleImageDelete = async (productId, imageId) => {
    try {
      await deleteData(`/upload/files/${imageId}`);

      const updatedProduct = {
        ...editingProduct,
        images: editingProduct.images.filter(img => img.id !== imageId)
      };

      setEditingProduct(updatedProduct);
      setProducts(prevProducts => 
        prevProducts.map(p => p.id === productId ? {
          ...p,
          images: updatedProduct.images
        } : p)
      );
    } catch (error) {
      console.error('Error handling image deletion:', error);
      alert("Erreur lors de la suppression de l'image");
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Produits loisirs</h2>
        <button 
          className="btn btn-primary"
          onClick={() => window.$('#addLoisirProductModal').modal('show')}
        >
          Ajouter un produit loisir
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {products.length === 0 ? (
            <p className="text-center text-muted my-5">Aucun produit loisir pour le moment</p>
          ) : (
            <LoisirProductTable 
              products={products}
              onEdit={handleEditClick}
              onDelete={handleDeleteProduct}
            />
          )}
        </div>
      </div>

      <AddLoisirProductModal
        newProduct={newProduct}
        setNewProduct={setNewProduct}
        onSubmit={handleAddProduct}
      />

      <EditLoisirProductModal
        editingProduct={editingProduct}
        setEditingProduct={setEditingProduct}
        onSubmit={handleUpdateProduct}
        onImageDelete={handleImageDelete}
      />
    </div>
  );
};

export default LoisirProducts;